import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const inputCls = "border-gold/20 bg-ink";

function FieldInput({ field, value, onChange, testId }) {
  const common = { id: testId, "data-testid": testId };
  switch (field.type) {
    case "textarea":
      return (
        <Textarea {...common} rows={4} required={field.required} placeholder={field.placeholder}
          value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1.5`} />
      );
    case "select":
      return (
        <select {...common} required={field.required} value={value} onChange={(e) => onChange(e.target.value)}
          className={`mt-1.5 h-10 w-full rounded-md border ${inputCls} px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-gold/50`}>
          <option value="">{field.placeholder || "Select…"}</option>
          {(field.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      );
    case "checkbox":
      return (
        <label className="mt-2 flex items-center gap-2.5 text-sm text-foreground/85">
          <input {...common} type="checkbox" checked={value === "Yes"} required={field.required}
            onChange={(e) => onChange(e.target.checked ? "Yes" : "")} className="h-4 w-4 accent-[#D4AF37]" />
          {field.placeholder || "Yes"}
        </label>
      );
    case "number":
      return (
        <Input {...common} type="number" min="1" max="50" required={field.required} placeholder={field.placeholder}
          value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1.5`} />
      );
    case "date":
      return (
        <Input {...common} type="date" required={field.required} value={value}
          onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1.5`} />
      );
    default:
      return (
        <Input {...common} type={field.type === "phone" ? "tel" : field.type === "email" ? "email" : "text"}
          required={field.required} placeholder={field.placeholder}
          value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} mt-1.5`} />
      );
  }
}

export default function DynamicForm({ form, eventId = null, onSuccess, initialData = null, testIdPrefix = "dynamic-form" }) {
  const [values, setValues] = useState(initialData || {});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post(`/forms/${form.id}/submit`, { event_id: eventId, data: values });
      setDone(true);
      onSuccess?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center py-8 text-center" data-testid={`${testIdPrefix}-success`}>
        <CheckCircle2 className="h-14 w-14 text-gold" />
        <p className="mt-5 max-w-sm font-display text-xl font-semibold leading-snug">
          {form.success_message || "Thank you! Your submission was received."}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={testIdPrefix} className="space-y-4">
      {(form.fields || []).map((f) => {
        const tid = `${testIdPrefix}-field-${String(f.id || f.label).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
        return (
          <div key={f.id || f.label}>
            <Label htmlFor={tid}>{f.label}{f.required ? " *" : ""}</Label>
            <FieldInput
              field={f}
              value={values[f.label] ?? ""}
              onChange={(v) => setValues({ ...values, [f.label]: v })}
              testId={tid}
            />
          </div>
        );
      })}
      <Button data-testid={`${testIdPrefix}-submit-button`} type="submit" disabled={loading}
        className="w-full rounded-full bg-gold py-5 font-semibold text-ink transition-all hover:bg-gold-hover">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : form.submit_label || "Submit"}
      </Button>
    </form>
  );
}
